import React, { useState, useEffect, useCallback } from 'react';
import { FaClock, FaExclamationTriangle, FaChartLine, FaCalculator } from 'react-icons/fa';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CostOfDelayCalculator = ({ 
  futureValue, 
  years, 
  expectedReturn, 
  existingCorpus, 
  originalSip,
  goalName = "Goal"
}) => {
  const [delayMonths, setDelayMonths] = useState(6);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState([]);

  const calculateDelay = useCallback(async (months) => {
    if (!futureValue || !years || !originalSip) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/calculate-delay-impact`, {
        future_value: futureValue,
        years: years,
        expected_return: expectedReturn || 12,
        existing_corpus: existingCorpus || 0,
        delay_months: months,
        original_sip: originalSip
      });
      setResult(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [futureValue, years, expectedReturn, existingCorpus, originalSip]);

  // Generate chart data for multiple delay scenarios
  useEffect(() => {
    const generateChartData = async () => {
      const delays = [0, 3, 6, 12, 18, 24, 36];
      const data = [];
      
      for (const delay of delays) {
        if (delay === 0) {
          data.push({
            delay: '0m',
            months: 0,
            sip: Math.round(originalSip),
            label: 'Start Now'
          });
        } else {
          const remainingYears = years - (delay / 12);
          if (remainingYears > 0) {
            // Simple calculation for chart (actual API call for selected delay)
            const monthlyRate = (expectedReturn || 12) / 100 / 12;
            const monthsRemaining = remainingYears * 12;
            const existingFv = (existingCorpus || 0) * Math.pow(1 + (expectedReturn || 12) / 100, remainingYears);
            const gap = futureValue - existingFv;
            const newSip = gap > 0 ? (gap * monthlyRate) / (Math.pow(1 + monthlyRate, monthsRemaining) - 1) : 0;
            
            data.push({
              delay: `${delay}m`,
              months: delay,
              sip: Math.round(newSip),
              label: delay >= 12 ? `${Math.floor(delay/12)}y ${delay%12 > 0 ? (delay%12) + 'm' : ''}` : `${delay}m`
            });
          }
        }
      }
      
      setChartData(data);
    };
    
    if (futureValue && years && originalSip) {
      generateChartData();
    }
  }, [futureValue, years, expectedReturn, existingCorpus, originalSip]);

  // Calculate on delay change
  useEffect(() => {
    calculateDelay(delayMonths);
  }, [delayMonths, calculateDelay]);

  const formatCurrency = (value) => {
    if (value === Infinity || !isFinite(value)) return 'N/A';
    return `₹${Math.round(value).toLocaleString('en-IN')}`;
  };

  const getBarColor = (months) => {
    if (months === 0) return '#34D399'; // Green for start now
    if (months <= 6) return '#FFD700'; // Gold for short delay
    if (months <= 12) return '#FB923C'; // Orange for medium delay
    return '#EF4444'; // Red for long delay
  };

  return (
    <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-orange-500">
      <div className="flex items-center space-x-3 mb-4">
        <FaClock className="text-orange-400" size={24} />
        <h3 className="text-xl font-bold text-orange-400">LIVE Cost of Delay Calculator</h3>
      </div>
      
      <p className="text-gray-400 text-sm mb-4">
        See how delaying your investment affects your monthly SIP requirement for {goalName}
      </p>

      {/* Delay Slider */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <label className="text-white font-medium">Delay Period</label>
          <span className="text-orange-400 font-bold text-xl">
            {delayMonths} months {delayMonths >= 12 && `(${(delayMonths/12).toFixed(1)} years)`}
          </span>
        </div>
        <input
          type="range"
          min="0"
          max="60"
          step="1"
          value={delayMonths}
          onChange={(e) => setDelayMonths(parseInt(e.target.value))}
          className="w-full h-3 bg-[#0A1929] rounded-lg appearance-none cursor-pointer slider-orange"
          style={{
            background: `linear-gradient(to right, #FFD700 0%, #FB923C ${delayMonths/60*50}%, #EF4444 ${delayMonths/60*100}%, #2D5A8F ${delayMonths/60*100}%, #2D5A8F 100%)`
          }}
        />
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>Start Now</span>
          <span>1 Year</span>
          <span>2 Years</span>
          <span>3 Years</span>
          <span>5 Years</span>
        </div>
      </div>

      {/* Impact Results */}
      {result && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-[#0A1929] rounded-lg p-4 border border-green-500/30">
            <p className="text-gray-400 text-xs mb-1">Original Monthly SIP</p>
            <p className="text-xl font-bold text-green-400">{formatCurrency(result.original_sip)}</p>
            <p className="text-gray-500 text-xs">If you start today</p>
          </div>
          
          <div className="bg-[#0A1929] rounded-lg p-4 border border-orange-500/30">
            <p className="text-gray-400 text-xs mb-1">New Monthly SIP</p>
            <p className="text-xl font-bold text-orange-400">{formatCurrency(result.new_sip)}</p>
            <p className="text-gray-500 text-xs">After {delayMonths}m delay</p>
          </div>
          
          <div className="bg-[#0A1929] rounded-lg p-4 border border-red-500/30">
            <p className="text-gray-400 text-xs mb-1">Monthly Increase</p>
            <p className="text-xl font-bold text-red-400">
              +{formatCurrency(result.sip_increase)}
            </p>
            <p className="text-red-400 text-xs font-medium">
              +{result.sip_increase_percent}% more
            </p>
          </div>
          
          <div className="bg-[#0A1929] rounded-lg p-4 border border-red-500/30">
            <p className="text-gray-400 text-xs mb-1">Extra Total Investment</p>
            <p className="text-xl font-bold text-red-400">
              {result.total_investment_increase > 0 ? '+' : ''}{formatCurrency(result.total_investment_increase)}
            </p>
            <p className="text-gray-500 text-xs">Over the period</p>
          </div>
        </div>
      )}

      {/* Warning Message */}
      {result && result.sip_increase > 0 && (
        <div className="bg-red-500/10 border border-red-500 rounded-lg p-4 mb-6">
          <div className="flex items-start space-x-3">
            <FaExclamationTriangle className="text-red-400 mt-1" size={18} />
            <div>
              <p className="text-red-400 font-semibold">{result.message}</p>
              <p className="text-gray-400 text-sm mt-1">
                Starting today saves you {formatCurrency(result.sip_increase)}/month and {formatCurrency(Math.abs(result.total_investment_increase))} in total investments.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Delay Impact Chart */}
      {chartData.length > 0 && (
        <div className="bg-[#0A1929] rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-4">
            <FaChartLine className="text-orange-400" />
            <p className="text-orange-400 font-semibold">Monthly SIP by Delay Period</p>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2D5A8F" />
              <XAxis 
                dataKey="label" 
                stroke="#9CA3AF" 
                tick={{ fontSize: 11 }} 
              />
              <YAxis 
                stroke="#9CA3AF" 
                tick={{ fontSize: 11 }}
                tickFormatter={(val) => `₹${(val/1000).toFixed(0)}K`}
              />
              <Tooltip 
                contentStyle={{ backgroundColor: '#1A2942', border: '1px solid #2D5A8F', borderRadius: '8px' }}
                labelStyle={{ color: '#FFD700' }}
                formatter={(value, name, props) => [
                  `₹${Math.round(value).toLocaleString('en-IN')}`,
                  'Monthly SIP'
                ]}
              />
              <Bar dataKey="sip" radius={[4, 4, 0, 0]}>
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getBarColor(entry.months)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <p className="text-gray-500 text-xs text-center mt-2">
            Green = Start now | Gold = Short delay | Orange = Medium delay | Red = Long delay
          </p>
        </div>
      )}

      {/* Quick Action */}
      <div className="mt-4 text-center">
        <p className="text-[#FFD700] text-lg font-bold">
          Every month of delay costs you ₹{Math.round((result?.sip_increase || 0) / Math.max(delayMonths, 1)).toLocaleString('en-IN')} extra per month!
        </p>
      </div>
    </div>
  );
};

export default CostOfDelayCalculator;
